import * as events from './event-bus';
import { SEARCH_ERRORS, DEFAULT_SEARCH_LIMIT} from './constants';
import _debounce from 'debounce';
import { httpClient } from './intercept-fluser';

/**
 * Fetch search results from the server and emit 'header.search.results'.
 * Supports two response formats:
 *  1. JSON array -> expects items with an `html` property; emits filtered array of html strings
 *  2. Plain text string OR JSON string -> emits the string directly
 * @param {string} endpoint API endpoint (base; query params appended automatically)
 * @param {string} searchKey User-entered search term (already trimmed by caller)
 * @param {Object} [options]
 * @param {number} [options.limit=20]
 * @param {number} [options.offset=0]
 * @param {Object} [options.queryParams] Additional static query params to include
 * @returns {Promise<string[]|string>} Resolves to an array of HTML snippet strings OR a string
 */
const getSearchResults = async (
	endpoint,
	searchKey,
	{ limit = 20, offset = 0, queryParams: extraParams } = {}
) => {
	if (!endpoint || !endpoint.trim()) {
		throw new Error(SEARCH_ERRORS.ENDPOINT_REQUIRED);
	}

	const params = new URLSearchParams({
		searchKey: searchKey || '',
		limit: String(limit),
		offset: String(offset),
	});

	if (extraParams && typeof extraParams === 'object') {
		Object.entries(extraParams).forEach(([k, v]) => {
			if (v !== undefined && v !== null) {
				params.set(k, String(v));
			}
		});
	}

	const url = `${endpoint}${
		endpoint.includes('?') ? '&' : '?'
	}${params.toString()}`;

	const response = await httpClient(url, { method: 'GET' });

	if (!response.ok) {
		const err = new Error(
			`${SEARCH_ERRORS.SEARCH_FETCH_FAILED} ${response.status}`
		);
		err.status = response.status; // eslint-disable-line no-param-reassign
		throw err;
	}

	const contentType = response.headers.get('content-type') || '';

	let searchData; // could become array or string
	if (contentType.includes('application/json')) {
		try {
			searchData = await response.json();
		} catch (e) {
			throw new Error(SEARCH_ERRORS.INVALID_SEARCH_RESPONSE_TYPE);
		}
	} else if (contentType.includes('text/plain')) {
		searchData = await response.text();
	} else {
		// Attempt a graceful fallback: try JSON parse, then text
		try {
			searchData = await response.json();
		} catch (e) {
			searchData = await response.text();
		}
	}

	let result;
	if (Array.isArray(searchData)) {
		result = searchData
			.map((item) => item && item.html)
			.filter((v) => typeof v === 'string' && v.trim() !== '');
	} else if (typeof searchData === 'string') {
		result = searchData;
	} else if (searchData && typeof searchData === 'object' && 'html' in searchData) {
		// Single object with html property
		result = searchData.html;
	} else {
		throw new Error(SEARCH_ERRORS.SEARCH_PARSE_FAILED);
	}

	events.emit('header.search.results', result);
	return result;
};

/**
 * Set up a debounced search handler that listens for 'header.search' events,
 * performs searches, and emits 'header.search.results' or 'header.search.error'.
 * @param {string} endpoint API endpoint (base; query params appended automatically)
 * @param {Object} [options]
 * @param {number} [options.debounceMs=350] Debounce delay in ms
 * @param {number} [options.minLength=1] Minimum search term length to trigger a search
 * @param {boolean} [options.clearOnShort=true] Whether to emit empty results if term is shorter than minLength
 * @param {number} [options.limit=20]
 * @param {number} [options.offset=0]
 * @param {Object} [options.queryParams] Additional static query params to include
 */
export const setupSearch = (endpoint, options = {}) => {
	const {
		debounceMs = 350,
		minLength = 1,
		clearOnShort = true,
		offset = 0,
		queryParams,
		limit,
	} = options;

	// if limit is null or undefined, use 20
	const effectiveLimit = limit ? limit : DEFAULT_SEARCH_LIMIT;
	const handler = _debounce((rawTerm) => performSearchRequest(endpoint, rawTerm, {
		minLength,
		clearOnShort,
		limit: effectiveLimit,
		offset,
		queryParams,
	}), debounceMs);
	events.on('header.search', handler);
	// Navigate when a search result item is clicked.
	events.on('header.search.item-clicked', (index, url) => {
		window.location.assign(url);
	});
};

/**
 * Execute a (possibly debounced) search request flow used by setupSearch.
 * Exposed for direct / test usage.
 * Emits:
 *  - 'header.search.results' (array|string)
 *  - 'header.search.error' (Error) on failure
 * @param {string} endpoint
 * @param {string} rawTerm
 * @param {Object} [options]
 * @param {number} [options.minLength=1]
 * @param {boolean} [options.clearOnShort=true]
 * @param {number} [options.limit=20]
 * @param {number} [options.offset=0]
 * @param {Object} [options.queryParams]
 * @returns {Promise<undefined|Array|string>} Returns emitted results (also emitted) or undefined if below minLength
 */
export const performSearchRequest = async (
	endpoint,
	rawTerm,
	{ minLength = 1, clearOnShort = true, limit = 20, offset = 0, queryParams } = {}
) => {
	const term = (rawTerm || '').trim();
	if (!term || term.length < minLength) {
		if (clearOnShort) {
			events.emit('header.search.results', []);
		}
		return undefined;
	}
	try {
		return await getSearchResults(endpoint, term, {
			limit,
			offset,
			queryParams,
		});
	} catch (err) {
		events.emit('header.search.error', err);
		return undefined;
	}
};
